import { useCallback, useEffect, useMemo, useState } from "react"
import { Clock3, LoaderCircle, MailPlus, ShieldCheck, Users, X } from "lucide-react"
import { toast } from "sonner"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { ChatbotFeaturePicker } from "@/components/editor/chatbot-feature-picker"
import { editorCardClass, editorDescriptionClass, editorHeadingClass } from "@/components/editor/editor-styles"
import { api, type ChatbotMember, type ChatbotMembersOverview } from "@/lib/api"
import { CHATBOT_SECTION_META, DEFAULT_INVITE_SECTIONS, grantableChatbotSections, orderedChatbotSections } from "@/lib/chatbot-sections"
import type { ChatbotSection } from "@/lib/types"

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short", year: "numeric" }).format(date)
}

function SectionChips({ sections }: { sections: ChatbotSection[] }) {
  if (!sections.length) return <Badge variant="outline" className="rounded-md border-warning/30 bg-warning/10 text-[10px] font-medium text-warning">Sem seções</Badge>
  return <>{sections.map((section) => <Badge key={section} variant="outline" className="rounded-md border-border bg-muted/40 text-[10px] font-medium text-muted-foreground">{CHATBOT_SECTION_META[section].label}</Badge>)}</>
}

export function UsersSection({ chatbotId }: { chatbotId: string }) {
  const [overview, setOverview] = useState<ChatbotMembersOverview>()
  const [email, setEmail] = useState("")
  const [inviteSections, setInviteSections] = useState<ChatbotSection[]>(DEFAULT_INVITE_SECTIONS)
  // Quem administra (dono, superadmin ou seção "Usuários") só concede o que tem.
  const grantableSections = useMemo(() => grantableChatbotSections(overview?.access), [overview?.access])
  const defaultInviteSections = useMemo(() => orderedChatbotSections(DEFAULT_INVITE_SECTIONS.filter((section) => grantableSections.includes(section))), [grantableSections])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [cancelingId, setCancelingId] = useState<string>()
  const [removingId, setRemovingId] = useState<string>()
  const [editing, setEditing] = useState<ChatbotMember>()
  const [editingSections, setEditingSections] = useState<ChatbotSection[]>([])
  const [savingPermissions, setSavingPermissions] = useState(false)
  const [error, setError] = useState<string>()

  const load = useCallback(async () => {
    setLoading(true)
    setError(undefined)
    try {
      setOverview(await api.getChatbotMembers(chatbotId))
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Não foi possível carregar os usuários.")
    } finally {
      setLoading(false)
    }
  }, [chatbotId])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    setInviteSections((current) => {
      const allowed = current.filter((section) => grantableSections.includes(section))
      return allowed.length ? allowed : defaultInviteSections
    })
  }, [defaultInviteSections, grantableSections])

  async function invite() {
    const normalized = email.trim()
    if (!normalized || !inviteSections.length) return
    setSending(true)
    try {
      await api.inviteChatbotMember(chatbotId, normalized, inviteSections)
      setEmail("")
      toast.success("Convite enviado por e-mail.")
      await load()
    } catch (requestError) {
      toast.error(requestError instanceof Error ? requestError.message : "Não foi possível enviar o convite.")
    } finally {
      setSending(false)
    }
  }

  async function cancel(invitationId: string) {
    setCancelingId(invitationId)
    try {
      await api.cancelChatbotInvitation(chatbotId, invitationId)
      toast.success("Convite cancelado.")
      await load()
    } catch (requestError) {
      toast.error(requestError instanceof Error ? requestError.message : "Não foi possível cancelar o convite.")
    } finally {
      setCancelingId(undefined)
    }
  }

  async function remove(userId: string) {
    setRemovingId(userId)
    try {
      await api.removeChatbotMember(chatbotId, userId)
      toast.success("Acesso removido.")
      await load()
    } catch (requestError) {
      toast.error(requestError instanceof Error ? requestError.message : "Não foi possível remover o acesso.")
    } finally {
      setRemovingId(undefined)
    }
  }

  async function savePermissions() {
    if (!editing || !editingSections.length) return
    setSavingPermissions(true)
    try {
      await api.updateChatbotMemberPermissions(chatbotId, editing.userId, editingSections)
      toast.success("Acesso atualizado.")
      setEditing(undefined)
      await load()
    } catch (requestError) {
      toast.error(requestError instanceof Error ? requestError.message : "Não foi possível atualizar o acesso.")
    } finally {
      setSavingPermissions(false)
    }
  }

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-40 rounded-2xl bg-card" /><Skeleton className="h-64 rounded-2xl bg-card" /></div>
  }

  return (
    <div className="space-y-5">
      {error && <Alert variant="destructive"><AlertDescription className="flex items-center justify-between gap-3"><span>{error}</span><Button variant="outline" size="sm" onClick={() => void load()}>Tentar novamente</Button></AlertDescription></Alert>}

      <section className={editorCardClass} aria-labelledby="invite-title">
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><MailPlus className="size-5" /></span>
          <div className="min-w-0">
            <h2 id="invite-title" className={editorHeadingClass}>Convidar para este assistente</h2>
            <p className={editorDescriptionClass}>Marque as seções que a pessoa pode abrir em{overview ? ` ${overview.chatbotName}` : " neste assistente"}. O convite expira em 7 dias.</p>
          </div>
        </div>
        {overview?.canManage ? (
          <form className="mt-5 space-y-4" onSubmit={(event) => { event.preventDefault(); void invite() }}>
            <div className="space-y-2">
              <Label htmlFor="chatbot-invite-email" className="text-[12px] font-medium">E-mail da pessoa</Label>
              <Input id="chatbot-invite-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="pessoa@empresa.com" className="field-bg h-11 rounded-xl border-border text-[13px]" disabled={sending} />
            </div>
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-[12px] font-medium"><ShieldCheck className="size-3.5 text-muted-foreground" />O que esta pessoa pode acessar</p>
              <ChatbotFeaturePicker idPrefix="invite-permissions" sections={inviteSections} onChange={setInviteSections} grantableSections={grantableSections} disabled={sending} />
            </div>
            <div className="flex justify-end">
              <Button type="submit" className="h-11 shrink-0 rounded-xl px-4 text-[13px]" disabled={sending || !email.trim() || !inviteSections.length}>{sending ? <LoaderCircle className="size-4 animate-spin" /> : <MailPlus className="size-4" />}{sending ? "Enviando..." : "Enviar convite"}</Button>
            </div>
          </form>
        ) : (
          <p className="mt-5 rounded-xl border border-info/30 bg-info/10 px-3.5 py-3 text-[12px] text-info">Você não tem acesso à gestão de usuários deste assistente.</p>
        )}
      </section>

      <section className={`${editorCardClass} !p-0 overflow-hidden`} aria-labelledby="members-title">
        <div className="border-b border-border px-5 py-4 sm:px-6"><h2 id="members-title" className="font-display text-[17px] font-bold">Usuários com acesso</h2><p className="muted mt-1 text-[11px]">{overview?.isOwner ? "O dono tem acesso total; os convidados, apenas as seções liberadas. Quem recebe a seção Usuários administra este quadro." : "Você administra os acessos deste assistente; o dono tem acesso total."}</p></div>
        <div className="divide-y divide-border">
          {overview?.members.length ? overview.members.map((member) => (
            <div key={member.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="min-w-0">
                <p className="truncate text-[13px] font-semibold"><Users className="mr-1.5 inline size-3.5 text-muted-foreground" />{member.name}{member.userId === overview.owner.id && <span className="muted ml-2 text-[11px] font-normal">(dono)</span>}</p>
                <p className="muted mt-0.5 truncate text-[11px]">{member.email}</p>
                <div className="mt-2 flex flex-wrap gap-1.5"><SectionChips sections={member.permissions} /></div>
              </div>
              {member.userId === overview.viewerId
                ? <span className="shrink-0 rounded-full border border-info/30 bg-info/10 px-2.5 py-1 text-[11px] font-medium text-info">Você</span>
                : overview.canManage && (
                  <div className="flex shrink-0 items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="h-8 rounded-lg border-border px-2.5 text-[11px] font-semibold" onClick={() => { setEditing(member); setEditingSections(member.permissions) }}>Editar acesso</Button>
                    <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0 text-muted-foreground hover:text-destructive" aria-label={`Remover acesso de ${member.email}`} title="Remover acesso" disabled={removingId === member.userId} onClick={() => void remove(member.userId)}>{removingId === member.userId ? <LoaderCircle className="size-4 animate-spin" /> : <X className="size-4" />}</Button>
                  </div>
                )}
            </div>
          )) : <p className="muted px-5 py-6 text-center text-[12px]">Nenhum convidado ainda. Envie o primeiro convite acima.</p>}
        </div>
      </section>

      {overview?.invitations.length ? (
        <section className={`${editorCardClass} !p-0 overflow-hidden`} aria-labelledby="pending-invites-title">
          <div className="border-b border-border px-5 py-4 sm:px-6"><h2 id="pending-invites-title" className="font-display text-[17px] font-bold">Convites pendentes</h2><p className="muted mt-1 text-[11px]">Aguardando a pessoa aceitar o e-mail enviado. Cancelar e reenviar permite trocar as seções.</p></div>
          <div className="divide-y divide-border">
            {overview.invitations.map((invitation) => (
              <div key={invitation.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-semibold">{invitation.email}</p>
                  <p className="muted mt-0.5 flex items-center gap-1.5 text-[11px]"><Clock3 className="size-3" />Expira em {formatDate(invitation.expiresAt)}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5"><SectionChips sections={invitation.permissions} /></div>
                </div>
                <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0 self-start text-muted-foreground hover:text-destructive sm:self-center" aria-label={`Cancelar convite para ${invitation.email}`} title="Cancelar convite" disabled={cancelingId === invitation.id} onClick={() => void cancel(invitation.id)}>{cancelingId === invitation.id ? <LoaderCircle className="size-4 animate-spin" /> : <X className="size-4" />}</Button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <Dialog open={Boolean(editing)} onOpenChange={(open) => { if (!open) setEditing(undefined) }}>
        <DialogContent className="bg-card rounded-2xl border-border p-6 sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="font-display text-[21px]">Acesso de {editing?.name}</DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground">Escolha o que {editing?.email} pode abrir neste assistente. As mudanças valem no próximo carregamento do painel dessa pessoa.</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <ChatbotFeaturePicker idPrefix="member-permissions" sections={editingSections} onChange={setEditingSections} grantableSections={grantableSections} disabled={savingPermissions} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-11 rounded-xl border-border" onClick={() => setEditing(undefined)}>Cancelar</Button>
            <Button className="h-11 rounded-xl" onClick={() => void savePermissions()} disabled={savingPermissions || !editingSections.length}>{savingPermissions && <LoaderCircle className="size-4 animate-spin" />}{savingPermissions ? "Salvando..." : "Salvar acesso"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
